import React from 'react'
import styled from '@emotion/styled'
import {
  KB,
  Chat,
  Chart,
  Widget,
  MySettings,
  Help,
  Inbox,
  Shortcuts,
  Crm,
} from '@groovehq/internal-design-system/lib/assets/icons'
import GlobalNav from '@groovehq/internal-design-system/lib/components/GlobalNav/GlobalNav'
import { divider } from '@groovehq/internal-design-system/lib/styles/elements'
import logoChristmas from '@groovehq/internal-design-system/lib/assets/images/lettermark-chrismas.png'
import storage from 'util/storage'
import { propFunc } from 'util/functions'

import { doOpenMainPage } from 'actions/pages'
import { LANDING_PAGE as KB_LANDING_PAGE } from 'subapps/kb/pages'
import { CHAT_PAGE as CHAT_LANDING_PAGE } from 'subapps/chat/pages'

import { SETTINGS_WIDGETS_PAGE } from 'subapps/settings/types'
import { REPORTS_MAIN_PAGE } from 'ducks/reports/pages'
import { INDEX_PAGE as CRM_INDEX_PAGE } from 'ducks/crm/pages'

import GlobalNavItem from './GlobalNavItem'
import UserItem from './UserItem'
import InboxLayoutSwitcher from './InboxLayoutSwitcher'
import { styles } from './styles'

const StyledUserItem = styled(UserItem)`
  margin-top: 8px;
  margin-left: 8px;
  margin-right: 8px;
`

const StyledInboxLayoutSwitcher = styled(InboxLayoutSwitcher)`
  margin-top: auto;
`

// Javascript with 0 based months...wtf
// so 11 actually means 12 here
const isDecember = new Date().getMonth() === 11

const currentLogo = isDecember ? logoChristmas : undefined

const toChatLandingPage = { type: CHAT_LANDING_PAGE }
const toKbLandingPage = { type: KB_LANDING_PAGE }
const toReportsLandingPage = { type: REPORTS_MAIN_PAGE }
const toWidgetLandingPage = { type: SETTINGS_WIDGETS_PAGE }
const toCrmLandingPage = { type: CRM_INDEX_PAGE }

export default class GlobalNavWrapper extends React.Component {
  componentDidMount() {
    const { setLeftSidebarOpenState, active } = this.props
    const isOpen = this.getLeftSidebarState(active)
    setLeftSidebarOpenState(isOpen)
  }

  getLeftSidebarState = subapp => {
    const storedStates = storage.get('leftSidebarOpenStates')
    return storedStates && storedStates[subapp] !== undefined
      ? storedStates[subapp]
      : true
  }

  rememberLeftSidebarState = subapp => {
    const { isLeftSidebarCollapsed } = this.props
    const storedStates = storage.get('leftSidebarOpenStates')

    storage.set('leftSidebarOpenStates', {
      ...storedStates,
      [subapp]: !!isLeftSidebarCollapsed,
    })
  }

  handleSubappClick = subapp => () => {
    const { setLeftSidebarOpenState } = this.props
    const isOpen = this.getLeftSidebarState(subapp)
    setLeftSidebarOpenState(isOpen)
  }

  handleWidgetClick = () => {
    if (window.groove && window.groove.widget) {
      window.groove.widget.toggle()
    }
  }

  render() {
    const {
      active,
      isInInbox,
      showHotkeysModal,
      noticeCounts,
      alerts,
      currentUserIsAdminOrOwner,
      isLeftSidebarCollapsed,
      isChatEnabled,
      settingsLandingPageAction,
      toggleLeftSidebar,
    } = this.props

    return (
      <GlobalNav
        isLeftSidebarCollapsed={isLeftSidebarCollapsed}
        doToggleLeftSidebar={toggleLeftSidebar}
        onClick={propFunc(this.rememberLeftSidebarState, active)}
        logo={currentLogo}
        logoStyles={isDecember ? styles.logo : undefined}
        onClickMenuButton={propFunc(this.rememberLeftSidebarState, active)}
        metaNav={
          <>
            <div css={divider.styles.primary} role="separator" />
            {isInInbox && (
              <>
                <StyledInboxLayoutSwitcher
                  isLeftSidebarCollapsed={isLeftSidebarCollapsed}
                />
                <GlobalNavItem
                  title="Shortcuts"
                  icon={<Shortcuts />}
                  onClick={showHotkeysModal}
                  noticeCount={noticeCounts.Shortcuts || null}
                  isLeftSidebarCollapsed={isLeftSidebarCollapsed}
                />
                <div
                  className="grui"
                  css={divider.styles.primary}
                  role="separator"
                />
              </>
            )}
            <GlobalNavItem
              title="Get help"
              icon={<Help />}
              onClick={this.handleWidgetClick}
              isLeftSidebarCollapsed={isLeftSidebarCollapsed}
            />
            <StyledUserItem />
          </>
        }
      >
        <GlobalNavItem
          to={doOpenMainPage()}
          title={app.t('Mailbox')}
          icon={<Inbox />}
          isActive={active === 'Inbox'}
          isLeftSidebarCollapsed={isLeftSidebarCollapsed}
          onClick={propFunc(this.handleSubappClick, 'Inbox')}
          noticeCount={noticeCounts.Inbox || null}
          alert={alerts.Inbox || null}
        />
        {isChatEnabled && (
          <GlobalNavItem
            to={toChatLandingPage}
            title="Live Chat"
            icon={<Chat />}
            isActive={active === 'Live Chat'}
            isLeftSidebarCollapsed={isLeftSidebarCollapsed}
            onClick={propFunc(this.handleSubappClick, 'Live Chat')}
            noticeCount={noticeCounts['Live Chat'] || null}
            alert={alerts['Live Chat'] || null}
          />
        )}
        <GlobalNavItem
          to={toKbLandingPage}
          title="Knowledge Base"
          icon={<KB />}
          isActive={active === 'Knowledge Base'}
          isLeftSidebarCollapsed={isLeftSidebarCollapsed}
          onClick={propFunc(this.handleSubappClick, 'Knowledge Base')}
          noticeCount={noticeCounts['Knowledge Base'] || null}
          alert={alerts['Knowledge Base'] || null}
        />
        <GlobalNavItem
          to={toReportsLandingPage}
          title="Reporting"
          icon={<Chart />}
          isActive={active === 'Reporting'}
          isLeftSidebarCollapsed={isLeftSidebarCollapsed}
          onClick={propFunc(this.handleSubappClick, 'Reporting')}
          noticeCount={noticeCounts.Reporting || null}
          alert={alerts.Reporting || null}
        />
        {currentUserIsAdminOrOwner && (
          <GlobalNavItem
            to={toWidgetLandingPage}
            title="Widget"
            icon={<Widget />}
            isActive={active === 'Widget'}
            isLeftSidebarCollapsed={isLeftSidebarCollapsed}
            onClick={propFunc(this.handleSubappClick, 'Widget')}
            noticeCount={noticeCounts.Widget || null}
            alert={alerts.Widget || null}
            featureDotFlag="showV3WidgetFeatureDot"
          />
        )}
        {localStorage.getItem('crm-enabled') && (
          <GlobalNavItem
            to={toCrmLandingPage}
            title="Address Book"
            icon={<Crm />}
            isActive={active === 'Address Book'}
            isLeftSidebarCollapsed={isLeftSidebarCollapsed}
            onClick={propFunc(this.handleSubappClick, 'CRM')}
          />
        )}
        <GlobalNavItem
          to={settingsLandingPageAction}
          title="Settings"
          icon={<MySettings />}
          isActive={active === 'Settings'}
          isLeftSidebarCollapsed={isLeftSidebarCollapsed}
          onClick={propFunc(this.handleSubappClick, 'Settings')}
          noticeCount={noticeCounts.Settings || null}
          featureDotFlag="showV2SettingsFeatureDot"
        />
      </GlobalNav>
    )
  }
}
